import axios from "axios";
import { useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import Footer from "../common/Footer";
import Header from "../common/Header";
import { organNames } from "../../helpers";
import level2Data from "../../data/level2.json";
import level3Data from "../../data/level3.json";
import level4Data from "../../data/level4.json";
import level5Data from "../../data/level5.json";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import HeaderPracticeQuestions from "../common/HeaderPracticeQuestions";




function InsertPracticeQuestion() {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", "", "", ""]);
  const [correctIndex, setCorrectIndex] = useState(null);
  const [explanation, setExplanation] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [questionError, setQuestionError] = useState("");
  const [explanationError, setExplanationError] = useState("");
  const [correctAnswerError, setCorrectAnswerError] = useState("");
  const { level1, level2, level3, level4 } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const tab = params.get("tab");

  const navigate = useNavigate();
  const { user } = useAuth();

  const handleOptionChange = (index, value) => {
    const updated = [...options];
    updated[index] = value;
    setOptions(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const plainText = explanation.replace(/<(.|\n)*?>/g, "").trim();
    const questionText = question.replace(/<(.|\n)*?>/g, "").trim();

    let hasError = false;

    if (!questionText) {
      setQuestionError("Question is required");
      hasError = true;
    } else {
      setQuestionError("");
    }

    if (!plainText) {
      setExplanationError("Explanation is required");
      hasError = true;
    } else {
      setExplanationError("");
    }

    if (correctIndex === null) {
      setCorrectAnswerError("Please select the correct answer");
      hasError = true;
    } else {
      setCorrectAnswerError("");
    }

    if (hasError) return;

    setDisabled(true);

    const payload = {
      level1,
      level2,
      level3,
      level4,
      question,
      options,
      tab:tab?tab:"",
      correctAnswer: {
        key: ["0", "1", "2", "3"][correctIndex],
        text: options[correctIndex],
      },
      explanation,
    };

    axios
      .post(
        `${process.env.REACT_APP_API_URL}/add-practice-question`,
        payload,
        { headers: { Authorization: "Bearer " + user.jwtToken } }
      )
      .then(() =>
        navigate(`/${level1}/${level2}/${level3}/${level4}/practice-questions`)
      )
      .catch(() => {
        setCorrectAnswerError("Something went wrong");
        setDisabled(false);
      });
  };

  return (
    <div className="bg-green5 page-wrapper w-100 min-vh-100 d-flex flex-column">
      <HeaderPracticeQuestions />

      <div className="row bg-green4 rounded-top-3 m-0 content-header position-relative">
        <div className="position-absolute d-flex w-100 top-0 start-50 translate-middle-x align-items-center justify-content-center text-green1 mt-1">
          <Link
            to={`/${level1}/${level2}/${level3}/${level4}`}
            className="btn btn-link text-decoration-none fw-bold"
          >
            <h5 className="fw-bold">
              {organNames(level2Data, level1, level2)}
              &nbsp;|&nbsp;
              {level3Data[`${level1}/${level2}/${level3}`]?.version}{" "}
              {level3Data[`${level1}/${level2}/${level3}`]?.name}
              &nbsp;|&nbsp;
              {level4Data[`${level1}/${level2}/${level3}/${level4}`]?.name}
            </h5>
          </Link>
        </div>
      </div>
      <main className="main-section mx-4 rounded-top-3 bg-white userList">
        <span className="fs-4 pt-4 pb-2 fw-bold lh-sm d-flex justify-content-center">
          Add New Practice Question
        </span>
        <div className="d-flex justify-content-center mt-4 mb-1">
          <form
            className="card shadow-sm p-4 bg-green4"
            style={{ width: "700px" }}
            onSubmit={handleSubmit}
          >
            {/* QUESTION */}
            <div className="mb-3 question-div">
              <label className="form-label fw-bold">Question</label>
              <ReactQuill
                theme="snow"
                value={question}
                onChange={(val) => {
                  setQuestion(val);
                  if (questionError) setQuestionError("");
                }}
                modules={{ toolbar: [["bold", "italic", "underline"]] }}
              />
              {questionError && (
                <div className="text-danger text-center mt-2">{questionError}</div>
              )}
            </div>
            {/* OPTIONS */}
            <label className="form-label fw-bold">Answer Options</label>

            {options.map((opt, i) => (
              <div key={i} className="input-group mb-2">
                <div className="input-group-text">
                  <input
                    type="radio"
                    name="correctAnswer"
                    checked={correctIndex === i}
                    onChange={() => {
                      setCorrectIndex(i);
                      if (correctAnswerError) setCorrectAnswerError(""); // 👈 clear error
                    }}
                  />
                </div>
                <input
                  type="text"
                  className="form-control"
                  placeholder={`Option ${String.fromCharCode(65 + i)}`}
                  value={opt}
                  onChange={(e) => handleOptionChange(i, e.target.value)}
                  required
                />
              </div>
            ))}
            {correctAnswerError && (
              <div className="text-danger text-center mt-2">
                {correctAnswerError}
              </div>
            )}
            {/* EXPLANATION */}
            <div className="mt-3">
              <label className="form-label fw-bold">Explanation</label>
              <ReactQuill
                theme="snow"
                value={explanation}
                onChange={(val) => {
                  setExplanation(val);
                  if (explanationError) setExplanationError("");
                }}
                modules={{ toolbar: [["bold", "italic", "underline"]] }}
              />
              {explanationError && (
                <div className="text-danger text-center mt-2">{explanationError}</div>
              )}
            </div>

            {/* ACTIONS */}
            <div className="d-flex justify-content-end gap-2 mt-4">
              <Link className="btn btn-primary"
                to={`/${level1}/${level2}/${level3}/${level4}/practice-questions`}
              >
                Cancel
              </Link>
              <button className="btn btn-secondary" disabled={disabled}>
                {disabled ? "Saving..." : "Save Question"}
              </button>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default InsertPracticeQuestion;
